import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../components/Header";
import BalanceDisplay from "../components/BalanceDisplay";
import Button from "../components/Button";
import AddIncomeModal from "../components/AddIncomeModal";
import AddExpenseModal from "../components/AddExpenseModal";
import AddBudgetModal from "../components/AddBudgetModal";
import { fetchLastBalance } from "../services/api";
import LayoutLoginHomepage from "../components/LayoutLoginHomepage";
import { useAuth } from "../context/AuthContext";


export default function HomePage() {
  const [balance, setBalance] = useState(0);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const menuRef = useRef();
  const { logout } = useAuth();

  const loadBalance = async () => {
    try {
      const data = await fetchLastBalance();
      if (data && typeof data.balance_amt !== "undefined" && data.balance_amt !== null) {
        // Delay setting balance to allow fade-in to complete first
        setTimeout(() => setBalance(data.balance_amt), 800);
      } else {
        setTimeout(() => setBalance(0), 800);
      }
    } catch (err) {
      console.error("Error fetching last balance:", err);
      setTimeout(() => setBalance(0), 800);
    }
  };

  useEffect(() => {
    // Trigger fade in animation first
    const timer = setTimeout(() => setIsLoaded(true), 100);
    loadBalance();
    return () => clearTimeout(timer);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  return (
    <LayoutLoginHomepage>
       <motion.div
        className="flex flex-col items-center justify-between relative overflow-hidden"
        style={{
          height: "100dvh",
          maxHeight: "100dvh",
          padding: "max(1rem, env(safe-area-inset-top)) 1rem max(1rem, env(safe-area-inset-bottom)) 1rem",
          boxSizing: "border-box",
          WebkitOverflowScrolling: "touch",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: isLoaded ? 1 : 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >

        {/* Top bar with Burger on left, Header centered */}
        <motion.div
          className="w-full flex items-center justify-between relative"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : -20 }}
          transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
        >
          {/* Left: Burger button */}
          <div className="absolute left-0">
            <button
              onClick={() => setMenuOpen(true)}
              className="text-white text-3xl sm:text-4xl p-2 focus:outline-none"
            >
              ☰
            </button>
          </div>

          {/* Center: Header */}
          <div className="flex-1 flex justify-center">
            <Header />
          </div>
        </motion.div>

        {/* Dark overlay */}
        {menuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setMenuOpen(false)}
          />
        )}

        {/* Sidebar Drawer */}
        <div
          ref={menuRef}
          className={`fixed top-0 left-0 h-full w-56 sm:w-64  bg-gray-900 shadow-lg transform transition-transform duration-300 ease-in-out z-50 ${menuOpen ? "translate-x-0" : "-translate-x-full"
            }`}
        >
          <div className="p-6">
            <button
              onClick={() => setMenuOpen(false)}
              className="text-gray-400 hover:text-white text-2xl mb-6"
            >
              ✕
            </button>
            <nav className="flex flex-col gap-6 text-xl text-white">
              <Link to="/budget" onClick={() => setMenuOpen(false)}>
                Budget
              </Link>
              <Link to="/balance/history" onClick={() => setMenuOpen(false)}>
                Balance History
              </Link>
              <button
              onClick={() => {
                logout();
                setMenuOpen(false);
              }}
              className="text-left text-xl text-red-400 hover:text-red-300 transition logoutButton"
            >
              Logout
            </button>
            </nav>
          </div>
        </div>

        {/* Page content */}
        <motion.div
          className="flex-1 flex flex-col items-center justify-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: isLoaded ? 1 : 0, scale: isLoaded ? 1 : 0.95 }}
          transition={{ duration: 0.6, delay: 0.8, ease: "easeOut" }}
        >
          <BalanceDisplay balance={balance} />
        </motion.div>

        <motion.div
          className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 w-full sm:w-auto bottomdiv"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 20 }}
          transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
        >
          <Button text="Add Income" color="green" onClick={() => setShowIncomeModal(true)} />
          <Button text="Add Expense" color="red" onClick={() => setShowExpenseModal(true)} />
        </motion.div>

        {showIncomeModal && (
          <AddIncomeModal onClose={() => setShowIncomeModal(false)} onSuccess={loadBalance} />
        )}
        {showBudgetModal && (
          <AddBudgetModal onClose={() => setShowBudgetModal(false)} onSuccess={() => setShowBudgetModal(false)} />
        )}
        {showExpenseModal && (
          <AddExpenseModal onClose={() => setShowExpenseModal(false)} onSuccess={loadBalance} />
        )}
      </motion.div>
    </LayoutLoginHomepage>
  );
}